import * as React from "react";
import { useParams } from "react-router-dom";

import { Alert, Badge, Button, Spinner } from "Components/ui";
import { Lock, RefreshCw, TriangleAlert } from "Components/ui/icons";
import { formatDate, formatINR } from "utils/format";
import { describePublicError, useStatementPageData } from "./data";
import PayShell from "./Components/PayShell";
import OtherWaysToPay from "./Components/OtherWaysToPay";

/**
 * Public /pay/s/:token page: everything the hospital owes now (what TeleOpd's warning shows) and
 * one "Pay" button. Paying it settles every invoice listed, so TeleOpd goes back to normal.
 */
const StatementPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { query, statement, checkout, redirecting, checkoutError } = useStatementPageData(token);

  if (query.isLoading) {
    return (
      <PayShell>
        <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
          <Spinner className="h-5 w-5" />
          Loading what's due…
        </div>
      </PayShell>
    );
  }

  if (!statement) {
    return (
      <PayShell>
        <div className="py-8 text-center">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-warning-subtle text-warning-text">
            <TriangleAlert className="h-6 w-6" aria-hidden="true" />
          </span>
          <p className="mt-4 text-base font-semibold text-foreground">
            We couldn't open this payment page
          </p>
          <p className="mt-1 text-sm text-muted-foreground">{describePublicError(query.error)}</p>
          <Button
            variant="secondary"
            leftIcon={<RefreshCw />}
            onClick={() => query.refetch()}
            className="mt-5"
          >
            Try again
          </Button>
        </div>
      </PayShell>
    );
  }

  const busy = checkout.isLoading || redirecting;
  const nothingDue = statement.amountDue <= 0;
  const canPay = statement.payable && !nothingDue;

  return (
    <PayShell payee={statement.payee}>
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-subtle-foreground">
          Amount due
        </p>
        <p className="mt-0.5 text-3xl font-semibold tabular-nums text-foreground">
          {formatINR(statement.amountDue)}
        </p>
        {statement.tenantName && (
          <p className="text-sm text-muted-foreground">{statement.tenantName}</p>
        )}
      </div>

      {statement.invoices.length > 0 && (
        <ul className="mt-5 divide-y divide-border rounded-card border border-border">
          {statement.invoices.map((line, index) => (
            <li
              key={line.invoiceNumber || `${line.dueDate}-${index}`}
              className="flex items-start justify-between gap-3 px-4 py-3"
            >
              <div className="min-w-0">
                <p className="break-words font-mono text-sm font-medium text-foreground">
                  {line.invoiceNumber || "Invoice"}
                </p>
                {line.title && <p className="text-xs text-muted-foreground">{line.title}</p>}
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Due {formatDate(line.dueDate)}
                </p>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1">
                <span className="text-sm font-semibold tabular-nums text-foreground">
                  {formatINR(line.amountDue)}
                </span>
                {line.overdue && (
                  <Badge tone="danger" dot>
                    Overdue
                  </Badge>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-6 space-y-3">
        {nothingDue && (
          <Alert tone="success" title="Nothing is due right now">
            Thank you! Your account is up to date.
          </Alert>
        )}
        {!nothingDue && !statement.payable && (
          <Alert tone="warning">
            Online payment isn't available right now. Please contact Meta16 Labs to pay by bank
            transfer, or try again later.
          </Alert>
        )}
        {!nothingDue && (
          <Button
            size="lg"
            fullWidth
            onClick={() => checkout.mutate()}
            disabled={!canPay || busy}
            loading={busy}
            leftIcon={<Lock />}
          >
            {redirecting
              ? "Opening secure checkout…"
              : `Pay ${formatINR(statement.amountDue)} securely`}
          </Button>
        )}
        {checkoutError && (
          <Alert tone="danger" title="Checkout didn't open">
            {checkoutError}
          </Alert>
        )}
        {canPay && (
          <p className="text-center text-xs text-muted-foreground">
            One payment covers every invoice above. UPI, cards and net banking; you'll come back
            here after paying.
          </p>
        )}
        {!nothingDue && (
          <OtherWaysToPay payee={statement.payee} amountLabel={formatINR(statement.amountDue)} />
        )}
      </div>
    </PayShell>
  );
};

export default StatementPage;
